/**
 * Projects API Service
 *
 * API client for project management, import sessions, and collections.
 */

import { API_BASE, getAuthHeaders, handleResponse } from './apiClient';

// ============================================
// Import Operations
// ============================================

/**
 * Import projects from GitHub to the backend
 *
 * @param {Object} user - Firebase user object
 * @param {Array} repos - Array of repository data objects
 * @param {Object} scope - Optional scope metadata (filters, source, etc.)
 * @returns {Promise<Object>} Import result with session_id and imported projects
 */
export const importProjects = async (user, repos, scope = null) => {
  try {
    const headers = await getAuthHeaders(user);

    // Map repos to API format
    const mappedRepos = repos.map(repo => ({
      name: repo.name,
      full_name: repo.fullName || repo.full_name || `${repo.owner}/${repo.name}`,
      description: repo.description,
      language: repo.language,
      stargazers_count: repo.stars || repo.stargazers_count,
      forks_count: repo.forks || repo.forks_count,
      topics: repo.topics || [],
      html_url: repo.url || repo.html_url,
      private: repo.isPrivate || repo.private || false,
      created_at: repo.createdAt || repo.created_at,
      updated_at: repo.updatedAt || repo.updated_at,
      pushed_at: repo.pushedAt || repo.pushed_at,
    }));

    const response = await fetch(`${API_BASE}/projects/import`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        repos: mappedRepos,
        scope: scope || {
          source: 'github',
          import_timestamp: new Date().toISOString(),
        },
      }),
    });

    return await handleResponse(response);
  } catch (error) {
    console.error('Error importing projects:', error);
    throw error;
  }
};

/**
 * List import sessions for the current user
 *
 * @param {Object} user - Firebase user object
 * @param {Object} options - Query options (limit, include_projects)
 * @returns {Promise<Array>} List of import sessions
 */
export const listImportSessions = async (user, options = {}) => {
  try {
    const headers = await getAuthHeaders(user);

    const params = new URLSearchParams();
    if (options.limit) params.set('limit', options.limit);
    if (options.include_projects) params.set('include_projects', 'true');

    const response = await fetch(`${API_BASE}/projects/import/sessions?${params}`, {
      method: 'GET',
      headers,
    });

    return await handleResponse(response);
  } catch (error) {
    console.error('Error listing import sessions:', error);
    throw error;
  }
};

/**
 * Get a specific import session
 *
 * @param {Object} user - Firebase user object
 * @param {string} sessionId - Import session UUID
 * @returns {Promise<Object>} Import session with projects
 */
export const getImportSession = async (user, sessionId) => {
  try {
    const headers = await getAuthHeaders(user);

    const response = await fetch(`${API_BASE}/projects/import/sessions/${sessionId}`, {
      method: 'GET',
      headers,
    });

    return await handleResponse(response);
  } catch (error) {
    console.error('Error getting import session:', error);
    throw error;
  }
};

/**
 * Get the last completed import session
 *
 * @param {Object} user - Firebase user object
 * @returns {Promise<Object>} Last import session with projects, or null
 */
export const getLastImportSession = async (user) => {
  try {
    const headers = await getAuthHeaders(user);

    const response = await fetch(`${API_BASE}/projects/import/last`, {
      method: 'GET',
      headers,
    });

    return await handleResponse(response);
  } catch (error) {
    console.error('Error getting last import session:', error);
    throw error;
  }
};

/**
 * Delete an import session
 *
 * @param {Object} user - Firebase user object
 * @param {string} sessionId - Import session UUID
 * @returns {Promise<Object>} Deletion result
 */
export const deleteImportSession = async (user, sessionId) => {
  try {
    const headers = await getAuthHeaders(user);

    const response = await fetch(`${API_BASE}/projects/import/sessions/${sessionId}`, {
      method: 'DELETE',
      headers,
    });

    return await handleResponse(response);
  } catch (error) {
    console.error('Error deleting import session:', error);
    throw error;
  }
};

// ============================================
// Projects Operations
// ============================================

/**
 * List all projects
 *
 * @param {Object} user - Firebase user object
 * @param {number} limit - Maximum projects to return
 * @returns {Promise<Object>} List of projects
 */
export const listProjects = async (user, limit = 50) => {
  try {
    const headers = await getAuthHeaders(user);
    const requestedLimit = Number.isFinite(limit) ? Math.max(1, limit) : 50;
    const pageSize = Math.min(requestedLimit, 100);
    const collectedProjects = [];
    let page = 1;
    let hasNext = true;

    while (hasNext && collectedProjects.length < requestedLimit) {
      const params = new URLSearchParams({
        page: String(page),
        page_size: String(pageSize),
        include_facets: 'false',
      });

      const response = await fetch(`${API_BASE}/projects/search?${params.toString()}`, {
        method: 'GET',
        headers,
      });

      const result = await handleResponse(response);
      const batch = Array.isArray(result?.projects) ? result.projects : [];
      collectedProjects.push(...batch);
      hasNext = Boolean(result?.has_next) && batch.length > 0;
      page += 1;
    }

    return {
      projects: collectedProjects.slice(0, requestedLimit),
      total: collectedProjects.length,
    };
  } catch (error) {
    console.error('Error listing projects:', error);
    throw error;
  }
};

/**
 * Get a project by name
 *
 * @param {Object} user - Firebase user object
 * @param {string} projectName - Project name
 * @returns {Promise<Object>} Project details
 */
export const getProject = async (user, projectName) => {
  try {
    const headers = await getAuthHeaders(user);

    const response = await fetch(`${API_BASE}/projects/${encodeURIComponent(projectName)}`, {
      method: 'GET',
      headers,
    });

    return await handleResponse(response);
  } catch (error) {
    console.error('Error getting project:', error);
    throw error;
  }
};

/**
 * Bulk delete projects from the database
 *
 * @param {Object} user - Firebase user object
 * @param {Array<number>} projectIds - Project IDs to delete
 * @returns {Promise<Object>} Deletion results with success/fail counts
 */
export const bulkDeleteProjects = async (user, projectIds) => {
  try {
    const headers = await getAuthHeaders(user);

    const response = await fetch(`${API_BASE}/bulk/projects`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        project_ids: projectIds,
        action: 'delete',
        options: { confirm_delete: true },
      }),
    });

    return await handleResponse(response);
  } catch (error) {
    console.error('Error bulk deleting projects:', error);
    throw error;
  }
};

export default {
  importProjects,
  listImportSessions,
  getImportSession,
  getLastImportSession,
  deleteImportSession,
  bulkDeleteProjects,
  listProjects,
  getProject,
};
